import API_CONFIG from '../config/api';

// Helper function to get authentication headers
const getAuthHeaders = (): { headers: { Authorization: string } } => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
};

// Interface for ticket attachment
export interface TicketAttachment {
  fileName: string;
  originalName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  uploadedAt?: string;
}

// Interface for ticket message
export interface TicketMessage {
  _id: string;
  sender: {
    _id: string;
    name: string;
    lastName: string;
  };
  senderType: 'user' | 'admin';
  message: string;
  attachments: TicketAttachment[];
  timestamp: string;
  isRead: boolean;
}

// Interface for ticket
export interface Ticket {
  _id: string;
  displayId?: number;
  ticketNumber: string;
  title: string;
  description: string;
  department: 'IT' | 'reception' | 'financial' | 'support';
  departmentDisplay: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  priorityDisplay: string;
  status: 'open' | 'in_progress' | 'waiting_user' | 'waiting_admin' | 'resolved' | 'closed';
  statusDisplay: string;
  userId: {
    _id: string;
    name: string;
    lastName: string;
    mobile: string;
  };
  assignedTo?: {
    _id: string;
    name: string;
    lastName: string;
  };
  attachments: TicketAttachment[];
  messages: TicketMessage[];
  resolution?: string;
  resolvedAt?: string;
  resolvedBy?: {
    _id: string;
    name: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
  lastActivity: string;
  unreadCount?: number;
}

// Interface for ticket creation
export interface CreateTicketRequest {
  title: string;
  description: string;
  department: string;
  priority?: string;
}

// Interface for pagination data
export interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

// Interface for ticket response
export interface TicketResponse {
  success: boolean;
  message?: string;
  data: {
    ticket?: Ticket;
    tickets?: Ticket[];
    pagination?: PaginationData;
  };
}

// Interface for ticket stats
export interface TicketStats {
  total: number;
  open: number;
  inProgress: number;
  waitingAdmin: number;
  resolved: number;
  closed: number;
  departments: Array<{
    department: string;
    departmentDisplay: string;
    count: number;
  }>;
  priorities: Array<{
    priority: string;
    priorityDisplay: string;
    count: number;
  }>;
}

// Create new ticket
export const createTicket = async (
  ticketData: CreateTicketRequest,
  attachments?: FileList
): Promise<TicketResponse> => {
  const formData = new FormData();
  
  // Add ticket data
  formData.append('title', ticketData.title);
  formData.append('description', ticketData.description);
  formData.append('department', ticketData.department);
  if (ticketData.priority) {
    formData.append('priority', ticketData.priority);
  }

  // Add attachments
  if (attachments) {
    for (let i = 0; i < attachments.length; i++) {
      formData.append('attachments', attachments[i]);
    }
  }

  const response = await fetch(`${API_CONFIG.baseURL}/tickets`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders().headers,
    },
    body: formData,
  });

  const data = await response.json();
  
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('احراز هویت ناموفق بود. لطفاً دوباره وارد شوید.');
    }
    throw new Error(data.message || 'خطا در ایجاد تیکت');
  }

  return data;
};

// Get user's tickets
export const getUserTickets = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  department?: string;
}): Promise<TicketResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.status) queryParams.append('status', params.status);
  if (params?.department) queryParams.append('department', params.department);

  const response = await fetch(
    `${API_CONFIG.baseURL}/tickets/my-tickets?${queryParams.toString()}`,
    {
      method: 'GET',
      ...getAuthHeaders(),
    }
  );

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'خطا در دریافت تیکت‌ها');
  }

  return data;
};

// Get all tickets (admin only)
export const getAllTickets = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  department?: string;
  priority?: string;
  assignedTo?: string;
}): Promise<TicketResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.status) queryParams.append('status', params.status);
  if (params?.department) queryParams.append('department', params.department);
  if (params?.priority) queryParams.append('priority', params.priority);
  if (params?.assignedTo) queryParams.append('assignedTo', params.assignedTo);

  const response = await fetch(
    `${API_CONFIG.baseURL}/tickets?${queryParams.toString()}`,
    {
      method: 'GET',
      ...getAuthHeaders(),
    }
  );

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'خطا در دریافت تیکت‌ها');
  }

  return data;
};

// Get ticket by ID
export const getTicketById = async (ticketId: string): Promise<TicketResponse> => {
  const response = await fetch(
    `${API_CONFIG.baseURL}/tickets/${ticketId}`,
    {
      method: 'GET',
      ...getAuthHeaders(),
    }
  );

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'خطا در دریافت تیکت');
  }

  return data;
};

// Add message to ticket
export const addMessage = async (
  ticketId: string,
  message: string,
  attachments?: FileList
): Promise<{ success: boolean; message: string }> => {
  const formData = new FormData();
  
  formData.append('message', message);

  // Add attachments
  if (attachments) {
    for (let i = 0; i < attachments.length; i++) {
      formData.append('attachments', attachments[i]);
    }
  }

  const response = await fetch(`${API_CONFIG.baseURL}/tickets/${ticketId}/messages`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders().headers,
    },
    body: formData,
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'خطا در ارسال پیام');
  }

  return data;
};

// Update ticket status (admin only)
export const updateTicketStatus = async (
  ticketId: string,
  status?: string,
  assignedTo?: string,
  resolution?: string,
  department?: string,
  priority?: string
): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(
    `${API_CONFIG.baseURL}/tickets/${ticketId}/status`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders().headers,
      },
      body: JSON.stringify({
        status,
        assignedTo,
        resolution,
        department,
        priority,
      }),
    }
  );

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'خطا در به‌روزرسانی تیکت');
  }

  return data;
};

// Get ticket statistics (admin only)
export const getTicketStats = async (): Promise<{
  success: boolean;
  data: TicketStats;
}> => {
  const response = await fetch(
    `${API_CONFIG.baseURL}/tickets/admin/stats`,
    {
      method: 'GET',
      ...getAuthHeaders(),
    }
  );

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'خطا در دریافت آمار تیکت‌ها');
  }

  return data;
};

// Helper function to format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to get file icon based on mime type
export const getFileIcon = (mimeType: string): string => {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType.includes('pdf')) return '📄';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
  if (mimeType.includes('zip') || mimeType.includes('rar')) return '📦';
  return '📎';
};